import React, { useState, useEffect, useRef } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import ReviewModal from "./ReviewModal";
import useCompletedInterviews from "../../hooks/useCompletedInterviews";
import { formatDateTime } from "../dateFormat";

const difficultyColors = {
  beginner: "text-green-600 bg-green-50",
  intermediate: "text-yellow-600 bg-yellow-50",
  advanced: "text-red-600 bg-red-50",
};

const CompletedInterviews = () => {
  const { completedInterviews, loadMore, hasMore, loading } = useCompletedInterviews();
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const scrollRef = useRef(null);

  const handleReviewClick = (interview) => {
    setSelectedInterview(interview);
    setIsReviewModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsReviewModalOpen(false);
    setSelectedInterview(null);
  };

  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loading) {
        loadMore();
      }
    };

    const container = scrollRef.current;
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [hasMore, loading, loadMore]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Past Interviews
      </h3>

      <div 
        ref={scrollRef}
        className="space-y-4 max-h-[500px] overflow-y-auto overflow-x-visible pr-2 custom-scrollbar"
        style={{ scrollBehavior: 'smooth' }}
      >
        {completedInterviews && completedInterviews.length > 0 ? (
          <>
            {completedInterviews.map((interview, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
          >
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-lg ${
                interview.status?.toLowerCase() === "completed"
                  ? "bg-green-100 text-green-600"
                  : interview.status?.toLowerCase() === "expired"
                  ? "bg-orange-100 text-orange-600"
                  : "bg-gray-100 text-gray-600"
              }`}>
                {SvgMapper.award("w-4 h-4")}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">
                  {interview.interview_name} - {formatDateTime(interview.active_from)} — {formatDateTime(interview.active_to)}
                </h4>
                <div className="flex items-center space-x-2 mt-2">
                  {interview.status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      interview.status?.toLowerCase() === "completed"
                        ? "text-green-600 bg-green-50"
                        : interview.status?.toLowerCase() === "expired"
                        ? "text-orange-600 bg-orange-50"
                        : "text-gray-600 bg-gray-50"
                    }`}>
                      {interview.status}
                    </span>
                  )}
                  {interview.category && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-50">
                      {interview.category}
                    </span>
                  )}
                  {interview.difficulty && (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        difficultyColors[interview.difficulty?.toLowerCase()] ||
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {interview.difficulty}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {(() => {
                const isExpired = interview?.status?.toLowerCase() === "expired";
                const hasReview = interview?.student_feedback;
                if (isExpired) {
                  return null;
                }
                
                return (
                  <div className="relative group">
                    <button 
                      onClick={() => hasReview && handleReviewClick(interview)}
                      disabled={!hasReview}
                      className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                        hasReview
                          ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                      }`}
                    >
                      {SvgMapper.eye("w-4 h-4")}
                      <span>Review</span>
                    </button>
                    {!hasReview && (
                      <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-[9999] shadow-lg">
                        Review not yet available
                        <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-gray-800"></div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
            ))}
            
            {loading && (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400"></div>
                <span className="ml-2 text-sm text-gray-600">Loading more...</span>
              </div>
            )}
            
            {!hasMore && completedInterviews.length > 0 && (
              <div className="text-center py-2 text-gray-500 text-sm">
                No more interviews to load
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">No past interviews</p>
        )}
      </div>
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={handleCloseModal}
        interview={selectedInterview}
      />
    </div>
  );
};

export default CompletedInterviews;
